// ---------------------------------------------------------------------------
// The category tabs.
//
// On a desktop till the categories sit in EXACTLY TWO ROWS - never a third,
// however many the shop adds - and the block scrolls SIDEWAYS when they run
// out of width. Every name is shown WHOLE: a tab is as wide as its words,
// with no ellipsis, because "Fresh ..." and "Ghan..." are indistinguishable
// to a cashier in a hurry.
//
// HOW THE TWO ROWS HAPPEN. One flat list of buttons in one container - not
// two containers, which would mean two `role="tablist"`s and a screen reader
// announcing the shop twice. The stylesheet turns the container into a grid
// with `grid-template-rows: repeat(2, auto)` and `grid-auto-flow: column`:
// the first tab goes top-left, the second beneath it, the third starts the
// next column. Two rows is a property of the GRID, so a third row is
// structurally impossible, and the overflow runs horizontally where a finger
// can swipe it. Below `sm` the container is a single scrolling line instead,
// because two rows of tabs on a phone is not a product grid any more.
//
// Two things a cashier notices when they are missing:
//
//   * The selected category scrolls itself into view. Choosing "Sandwiches",
//     changing screen, and coming back to a row scrolled to the far left with
//     no visible sign of which tab is active is disorienting.
//   * Arrows appear only when there IS something to scroll to - which after
//     this change means only on a phone.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { useI18n } from '../../i18n/I18nProvider';

export interface CategoryRailItem {
  id: string | null;
  label: string;
  count?: number;
  color?: string | null;
}

export function CategoryRail({
  items,
  activeId,
  onSelect,
  ariaLabel,
}: {
  items: CategoryRailItem[];
  activeId: string | null;
  onSelect: (id: string | null) => void;
  ariaLabel: string;
}) {
  const { t, dir } = useI18n();
  const trackRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  const [overflowing, setOverflowing] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  /// Where the track is, in a way that works in both directions.
  ///
  /// In RTL browsers report `scrollLeft` as zero-at-the-right or as a negative
  /// number depending on the engine, so the ends are worked out from absolute
  /// distances rather than from the sign.
  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const slack = track.scrollWidth - track.clientWidth;
    // A pixel of slack is rounding, not something to scroll.
    setOverflowing(slack > 2);

    const offset = Math.abs(track.scrollLeft);
    setAtStart(offset <= 2);
    setAtEnd(offset >= slack - 2);
  }, []);

  useLayoutEffect(() => {
    measure();

    const track = trackRef.current;
    if (!track) return;

    // Re-measured when the COLUMN resizes, not when the window does: opening
    // the cart drawer or collapsing the sidebar changes this row's width
    // without the window changing at all.
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    return () => observer.disconnect();
  }, [measure, items.length]);

  useEffect(() => {
    // Optional-called: `scrollIntoView` is missing in jsdom and in a few
    // embedded browsers, and a category row that throws is worse than one
    // that does not scroll itself.
    activeRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [activeId]);

  const nudge = (direction: 'back' | 'forward') => {
    const track = trackRef.current;
    if (!track) return;

    // Most of a screenful, so nothing is skipped over unseen.
    const step = Math.max(160, track.clientWidth * 0.8);
    const towardsEnd = direction === 'forward';
    const sign = dir === 'rtl' ? -1 : 1;

    track.scrollBy({ left: (towardsEnd ? step : -step) * sign, behavior: 'smooth' });
  };

  return (
    // `min-w-0` here is the whole trick: this wrapper may shrink, so the track
    // inside it scrolls instead of the page.
    <div className="relative flex min-w-0 shrink-0 items-start gap-1">
      {overflowing ? (
        <RailButton
          icon={<ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />}
          label={t.pos.scrollCategoriesBack}
          disabled={atStart}
          onClick={() => nudge('back')}
        />
      ) : null}

      <div
        ref={trackRef}
        onScroll={measure}
        role="tablist"
        aria-label={ariaLabel}
        className="scrollbar-brand category-tabs min-w-0 flex-1"
      >
        {items.map((item) => {
          const active = activeId === item.id;

          return (
            <button
              key={item.id ?? 'all'}
              ref={active ? activeRef : undefined}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSelect(item.id)}
              className={clsx(
                // Height, padding and radius are the same on every tab in
                // both rows, so the block reads as one control rather than as
                // two rows that happen to be near each other.
                'flex h-11 items-center justify-center gap-2 rounded-control border px-3 text-sm font-semibold transition-colors',
                active
                  ? 'border-butcher-green bg-butcher-green text-white'
                  : 'border-line-strong bg-surface text-ink hover:bg-surface-sunken',
              )}
            >
              {/* A colour chip identifies the category, but the name is always
                  present - colour alone would exclude a colour-blind cashier. */}
              {item.color && !active ? (
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: item.color }}
                  aria-hidden="true"
                />
              ) : null}

              {/* The WHOLE name, on one line. Never truncated: the tab takes
                  the width its words need, and the container scrolls. */}
              <span className="whitespace-nowrap">{item.label}</span>

              {item.count !== undefined ? (
                <span
                  className={clsx(
                    'numeric shrink-0 rounded-full px-1.5 py-0.5 text-[10px]',
                    active ? 'bg-white/20' : 'bg-surface-sunken text-ink-muted',
                  )}
                >
                  {item.count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {overflowing ? (
        <RailButton
          icon={<ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />}
          label={t.pos.scrollCategoriesForward}
          disabled={atEnd}
          onClick={() => nudge('forward')}
        />
      ) : null}
    </div>
  );
}

/// Hidden from touch devices, where swiping is the natural gesture and an
/// arrow is just something else to hit by accident.
function RailButton({
  icon,
  label,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={clsx(
        'hidden h-11 w-8 shrink-0 items-center justify-center rounded-control border border-line-strong bg-surface text-ink-muted transition-colors',
        'hover:bg-surface-sunken disabled:opacity-30 [@media(pointer:fine)]:flex',
      )}
    >
      {icon}
    </button>
  );
}
